import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { Box, TableCell, Typography } from '@mui/material';
import type { ThemeOptions } from '@mui/material/styles';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import type {
  MUIDataTableColumn,
  MUIDataTableColumnOptions,
  MUIDataTableCustomHeadRenderer,
  MUIDataTableProps,
} from 'mui-datatables';
import MUIDataTable from 'mui-datatables';
import React, { useEffect, useState } from 'react';

import Icon from '@components/Icon/Icon';

import { useApp } from '@hooks/useApp';
import { useIsMobile } from '@hooks/useIsMobile';
import { useTablePagination } from '@hooks/useTablePagination';

import { formatFhirDate } from '@utils/fhir';

import { baseThemeOptions } from '../../../theme';
import Palette from '../../../theme/palette';
import { ArrowContainer } from '../DataTable.style';
import { CustomFilterIcon } from './CustomFilterIcon';
import CustomFooter from './CustomFooter';
import {
  dateColumnOptions,
  defaultColumnOptions,
  defaultTableOptions,
} from './options';
import { useStyles } from './styles';
import { tableThemeOptions } from './tableThemeOptions';

type ClassName =
  | 'blue-hover'
  | 'small'
  | 'small-light'
  | 'default'
  | 'date'
  | 'status'
  | 'hover-only';

interface DataTableProps extends Omit<MUIDataTableProps, 'columns' | 'title'> {
  columns: (MUIDataTableColumn & {
    className?: ClassName;
    desktopOnly?: boolean;
  })[];
  title?: React.ReactNode;
  themeOptions?: ThemeOptions;
  /**
   * Opt in to URL-based pagination persistence. When provided, page, page size,
   * and search text are synced to URL query params so the user's position is
   * restored on back navigation and page refresh.
   *
   * Use a unique value per table when multiple tables share a page:
   *   pageParam='clients' → ?clients=2&clientsSize=50
   *   pageParam='discern'  → ?discern=2&discernSize=50
   *
   * Omit for tables that manage their own pagination (serverSide, embedded,
   * modal) — those tables are completely unaffected.
   */
  pageParam?: string;
}

function renderDateValue(value: Date) {
  return formatFhirDate(value.toISOString(), {
    showTime: false,
  });
}

const muiCache = createCache({
  key: 'mui-datatables',
  prepend: true,
});

function MuiDataTable({
  columns,
  data,
  title,
  options,
  themeOptions = {},
  pageParam,
}: DataTableProps) {
  const isMobile = useIsMobile();
  const {
    state: { themeMode },
  } = useApp();
  const { classes } = useStyles();

  const getMuiTheme = () =>
    createTheme(
      deepmerge(
        {
          ...baseThemeOptions,
          palette: themeMode === 'dark' ? Palette.dark : Palette.light,
        },
        tableThemeOptions
      ),
      themeOptions
    );

  // Only active when caller explicitly passes pageParam — hook must be called
  // unconditionally, so we fall back to a dummy key and discard the result.
  const urlPagination = useTablePagination(pageParam ?? '__noop__');
  const urlSyncEnabled = pageParam !== undefined;

  // Track page/rowsPerPage in local state so options.page always equals the
  // user's current position. These are updated inside the customFooter wrapper
  // on the same click event as MUIDataTable's own setState — React 18 batches
  // them together, so componentDidUpdate always sees the correct options.page
  // and setTableData never resets the user's position with a stale value.
  const [syncPage, setSyncPage] = useState(
    urlSyncEnabled ? urlPagination.initialPage : 0
  );
  const [syncRowsPerPage, setSyncRowsPerPage] = useState(
    urlSyncEnabled ? urlPagination.initialRowsPerPage : 25
  );

  // Keep syncPage/syncRowsPerPage in sync with the URL when the user presses
  // browser back/forward while still on this page (popstate changes the URL
  // without remounting the component, so useState initializers don't re-run).
  useEffect(() => {
    if (urlSyncEnabled) setSyncPage(urlPagination.initialPage);
  }, [urlSyncEnabled, urlPagination.initialPage]);

  useEffect(() => {
    if (urlSyncEnabled) setSyncRowsPerPage(urlPagination.initialRowsPerPage);
  }, [urlSyncEnabled, urlPagination.initialRowsPerPage]);

  // Clamp out-of-range page to the last valid page once data is available
  // (e.g. ?labs=999 when there are only 3 pages of data).
  const dataLength = Array.isArray(data) ? data.length : 0;
  useEffect(() => {
    if (!urlSyncEnabled || dataLength === 0) return;
    const lastPage = Math.max(0, Math.ceil(dataLength / syncRowsPerPage) - 1);
    if (syncPage > lastPage) {
      setSyncPage(lastPage);
      // replace so ?labs=999 doesn't push a new history entry that re-triggers the clamp on Back
      urlPagination.setPage(lastPage, true);
    }
    // urlPagination.setPage wraps navigate() — omitting it avoids re-running on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLength, syncPage, syncRowsPerPage, urlSyncEnabled]);

  return (
    <CacheProvider value={muiCache}>
      <ThemeProvider theme={getMuiTheme()}>
        <MUIDataTable
          columns={columns
            .filter(({ desktopOnly = false }) => {
              if (isMobile) {
                return !desktopOnly;
              }
              return true;
            })
            .map(({ className = 'default', name, label, options }) => {
              const isDate = className === 'date';
              return {
                name,
                label,
                options: {
                  searchable: false,
                  sort: false,
                  filter: false,
                  ...(isDate ? dateColumnOptions : {}),
                  ...options,
                  filterOptions: {
                    fullWidth: isMobile,
                    ...(isDate ? dateColumnOptions.filterOptions : {}),
                    renderValue: (value: any) => {
                      if (value instanceof Date) {
                        return renderDateValue(value);
                      }
                      const valueType = typeof value;
                      switch (valueType) {
                        case 'string':
                          // eslint-disable-next-line no-case-declarations
                          const trimmed = value.trim();
                          return trimmed && trimmed !== '-' ? value : '(None)';
                        default:
                          return value;
                      }
                    },
                    ...options?.filterOptions,
                  },
                  customHeadRender: (
                    columnMeta: MUIDataTableCustomHeadRenderer &
                      Pick<MUIDataTableColumnOptions, 'customHeadLabelRender'>,
                    handleToggleColumn,
                    sortOrder
                  ) => {
                    const { index, label, name, customHeadLabelRender } =
                      columnMeta;
                    if (options?.customHeadRender) {
                      return options.customHeadRender(
                        columnMeta,
                        handleToggleColumn,
                        sortOrder
                      );
                    }
                    const showArrow = columnMeta.name === sortOrder.name;
                    const headLabel =
                      typeof customHeadLabelRender === 'function'
                        ? customHeadLabelRender({
                            ...columnMeta,
                            colPos: index,
                          })
                        : label;
                    return (
                      <TableCell
                        key={`th-${index}-${name}`}
                        onClick={() => handleToggleColumn(index)}
                      >
                        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                          <Typography variant="h6">{headLabel}</Typography>
                          {showArrow ? (
                            <ArrowContainer
                              inverted={sortOrder.direction === 'asc'}
                            >
                              <Icon
                                clickable
                                color="text.primary"
                                name="chevron-down"
                              />
                            </ArrowContainer>
                          ) : null}
                        </Box>
                      </TableCell>
                    );
                  },
                  customBodyRender: (value, ...args) => {
                    if (value instanceof Date) {
                      return renderDateValue(value);
                    }
                    const trimmed =
                      typeof value === 'string' ? value.trim() : value;
                    const rendered =
                      options?.customBodyRender?.(trimmed, ...args) ??
                      (trimmed ? trimmed : '-');
                    if (
                      isMobile &&
                      (rendered === null ||
                        rendered === undefined ||
                        rendered === '')
                    ) {
                      return null;
                    }
                    return rendered;
                  },
                  setCellProps: (...args) => ({
                    ...options?.setCellProps?.(...args),
                    className: `${classes.cell} ${
                      className in classes ? classes[className] : ''
                    }`,
                  }),
                },
              };
            })}
          data={data}
          options={{
            ...defaultTableOptions,
            ...defaultColumnOptions,
            ...options,
            ...(urlSyncEnabled && {
              page: syncPage,
              rowsPerPage: syncRowsPerPage,
              // Call navigate directly from the click event — before any
              // MUIDataTable setState processing — so the URL is always correct
              // regardless of setTableData race conditions.
              customFooter: (
                rowCount,
                page,
                rowsPerPage,
                changeRowsPerPage,
                changePage
              ) => (
                <CustomFooter
                  changePage={(newPage: number) => {
                    setSyncPage(newPage);
                    urlPagination.setPage(newPage);
                    changePage(newPage);
                  }}
                  changeRowsPerPage={(rows: string | number) => {
                    const n =
                      typeof rows === 'string' ? parseInt(rows, 10) : rows;
                    setSyncRowsPerPage(n);
                    setSyncPage(0);
                    urlPagination.setRowsPerPage(n);
                    changeRowsPerPage(rows);
                  }}
                  page={page}
                  rowCount={rowCount}
                  rowsPerPage={rowsPerPage}
                />
              ),
              onTableChange: (action, tableState) => {
                switch (action) {
                  case 'search':
                    urlPagination.setSearchText(tableState.searchText ?? '');
                    break;
                  case 'onSearchClose':
                    urlPagination.setSearchText('');
                    break;
                }
                options?.onTableChange?.(action, tableState);
              },
            }),
            setRowProps: () => ({
              className: classes.row,
            }),
          }}
          components={{
            icons: {
              // @ts-expect-error Type def is wrong
              FilterIcon: CustomFilterIcon,
            },
          }}
          title={title}
        />
      </ThemeProvider>
    </CacheProvider>
  );
}

export default MuiDataTable;
